import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import { onboardingService } from '../services/onboardingService'
import {
  FlowType,
  FlowVariant,
  OnboardingProgress,
  ResumePoint,
  StepFormData,
  TimeAnalytics,
  OnboardingContextType,
} from '../types/onboarding.types'

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
)

interface OnboardingProviderProps {
  children: ReactNode
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null)
  const [resumePoint, setResumePoint] = useState<ResumePoint | null>(null)
  const [analytics, setAnalytics] = useState<TimeAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // ==========================================
  // ACCIONES
  // ==========================================

  const initializeFlow = useCallback(
    async (flowType: FlowType, variant: FlowVariant) => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await onboardingService.initializeFlow(flowType, variant)
        setProgress(result)
        setIsInitialized(true)

        console.log('✅ Flujo inicializado:', result)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Error al inicializar flujo'
        setError(message)
        console.error('❌ Error inicializando flujo:', error)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const refreshProgress = useCallback(async () => {
    if (!isInitialized) return

    setIsLoading(true)
    setError(null)

    try {
      const currentProgress = await onboardingService.getProgress()
      setProgress(currentProgress)

      console.log('✅ Progreso actualizado:', currentProgress)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al obtener progreso'
      setError(message)
      console.error('❌ Error obteniendo progreso:', error)
    } finally {
      setIsLoading(false)
    }
  }, [isInitialized])

  const completeStep = useCallback(
    async (stepKey: string, stepData: StepFormData, notes?: string) => {
      setIsLoading(true)
      setError(null)

      try {
        await onboardingService.completeStep(stepKey, stepData, notes)

        // Actualizar progreso después de completar
        await refreshProgress()

        console.log(`✅ Paso completado: ${stepKey}`)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Error al completar paso'
        setError(message)
        console.error(`❌ Error completando paso ${stepKey}:`, error)
        throw error // Re-lanzar para que el componente pueda manejarlo
      } finally {
        setIsLoading(false)
      }
    },
    [refreshProgress]
  )

  const enterStep = useCallback(
    async (stepKey: string, metadata?: Record<string, any>) => {
      try {
        await onboardingService.enterStep(stepKey, metadata)
        console.log(`✅ Entrada registrada: ${stepKey}`)
      } catch (error) {
        console.warn(`⚠️ Error entrando a paso ${stepKey}:`, error)
        // No establecer error en el estado para no bloquear navegación
      }
    },
    []
  )

  const exitStep = useCallback(async (stepKey: string) => {
    try {
      await onboardingService.exitStep(stepKey)
      console.log(`✅ Salida registrada: ${stepKey}`)
    } catch (error) {
      console.warn(`⚠️ Error saliendo de paso ${stepKey}:`, error)
      // No establecer error en el estado para no bloquear navegación
    }
  }, [])

  const checkResumePoint = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const resume = await onboardingService.getResumePoint()
      setResumePoint(resume)

      if (resume.canResume) {
        setIsInitialized(true)
        console.log('✅ Punto de reanudación encontrado:', resume)
      }
    } catch {
      console.log('ℹ️ No hay progreso previo para reanudar')
      setResumePoint(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadAnalytics = useCallback(async () => {
    if (!isInitialized) return

    try {
      const analyticsData = await onboardingService.getTimeAnalytics()
      setAnalytics(analyticsData)
      console.log('✅ Analytics cargados:', analyticsData)
    } catch (error) {
      console.warn('⚠️ Error cargando analytics:', error)
    }
  }, [isInitialized])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // ==========================================
  // EFECTOS
  // ==========================================

  // Verificar progreso existente al montar - solo si hay token de autenticación
  useEffect(() => {
    // Only check resume point if user is authenticated
    const token = localStorage.getItem('auth_token')
    if (token) {
      checkResumePoint()
    }
  }, [checkResumePoint])

  // Cargar analytics cuando se inicializa
  useEffect(() => {
    if (isInitialized) {
      loadAnalytics()

      // Actualizar analytics cada 2 minutos
      const interval = setInterval(loadAnalytics, 2 * 60 * 1000)
      return () => clearInterval(interval)
    }
  }, [isInitialized, loadAnalytics])

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      onboardingService.cleanup()
    }
  }, [])

  // ==========================================
  // PROVIDER
  // ==========================================

  const value: OnboardingContextType = {
    // Estado
    progress,
    resumePoint,
    analytics,
    isLoading,
    error,
    isInitialized,

    // Acciones
    initializeFlow,
    refreshProgress,
    completeStep,
    enterStep,
    exitStep,
    checkResumePoint,
    loadAnalytics,
    clearError,
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

// ==========================================
// HOOK PERSONALIZADO
// ==========================================

export const useOnboardingContext = (): OnboardingContextType => {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error(
      'useOnboardingContext must be used within OnboardingProvider'
    )
  }
  return context
}
